import { Fragment, useMemo, useState } from 'react'

import { AlertTriangle, ArrowUpDown, Layers } from 'lucide-react'

import type { IssueSeverity } from '@/shared/constants/issue-status'

import { ContactPill } from './ContactPill'
import { DetailChip } from './DetailChips'
import { DetailOverlay } from './DetailShell'
import { DetailStatStrip, type DetailStat } from './DetailStatStrip'
import { useGisIssues } from '../../../hooks/useGisIssues'
import { useInlineIssueAccordion } from '../../../hooks/useInlineIssueAccordion'
import { useResolutionTargets } from '../../../hooks/useResolutionTargets'
import { buildDepartmentWorkload, sortWorkloadRows, type WorkloadRow, type WorkloadSort } from '../../../lib/department-workload'
import { prettyName } from '../../../lib/mandal-name'
import { IssueInlineDetail } from '../issues/IssueInlineDetail'

import type { GisScope, GisSite } from '../../../api/types'
import type { HodOpenRef } from '../../../lib/detail-stack'

const SEV_TOKEN: Record<IssueSeverity, string> = { critical: 'bg-gis-red', non_critical: 'bg-gis-orange' }
const NEXT: Record<WorkloadSort, WorkloadSort> = { aging: 'severity', severity: 'status', status: 'aging' }
const SORT_LABEL: Record<WorkloadSort, string> = { aging: 'Aging', severity: 'Severity', status: 'Status' }

function initials(n: string): string {
  const x = n.replace(/[^A-Za-z ]/g, '').trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase()
  return x || '?'
}

interface HodDetailProps {
  department: string
  hodName?: string | null | undefined
  hodPhone?: string | null | undefined
  hodEmail?: string | null | undefined
  scope: GisScope
  sites: GisSite[]
  backLabel: string
  onBack: () => void
  onOpenHod: (hod: HodOpenRef) => void
  onSelectOfficer: (id: string, name: string) => void
  onSelectMandal: (name: string) => void
}

/** HOD identity hero: avatar + role/name/workload, with the phone/email contacts stacked BELOW (full
 *  width, `sm` font) so long pills never squeeze or overlap the name in a narrow drawer. */
function HodHero({
  department,
  hodName,
  hodPhone,
  hodEmail,
}: {
  department: string
  hodName?: string | null | undefined
  hodPhone?: string | null | undefined
  hodEmail?: string | null | undefined
}) {
  return (
    <div className="mb-4 rounded-2xl bg-white px-[18px] py-[15px] shadow-gis-card">
      <div className="flex items-center gap-[14px]">
        <span className="flex h-12 w-12 flex-none items-center justify-center rounded-[13px] bg-[linear-gradient(140deg,#1b2c52,#0f1f3d)] text-base font-bold text-white">
          {hodName ? initials(hodName) : department.slice(0, 2).toUpperCase()}
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[10px] font-bold tracking-[.05em] text-gis-subheading uppercase">Head of Dept · {department}</div>
          <div className="mt-0.5 truncate text-[16px] font-bold tracking-[-.01em] text-gis-ink">{hodName ?? department}</div>
          <div className="mt-px text-[11.5px] font-medium text-gis-soft">Department workload</div>
        </div>
      </div>
      {(hodPhone || hodEmail) && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          <ContactPill phone={hodPhone ?? null} email={hodEmail ?? null} size="sm" />
        </div>
      )}
    </div>
  )
}

export function HodDetail({ department, hodName, hodPhone, hodEmail, scope, sites, backLabel, onBack, onOpenHod, onSelectOfficer, onSelectMandal }: HodDetailProps) {
  const targets = useResolutionTargets()
  const { data, isPending } = useGisIssues(scope)
  const [sort, setSort] = useState<WorkloadSort>('aging')
  const w = useMemo(
    () => buildDepartmentWorkload(data?.issues ?? [], sites, department, targets),
    [data, sites, department, targets],
  )
  const rows = useMemo(() => sortWorkloadRows(w.rows, sort), [w.rows, sort])
  const { expandedId, toggle, collapse, rootRef } = useInlineIssueAccordion(rows.map((r) => r.id))

  const stats: DetailStat[] = [
    { label: 'Unresolved', value: w.unresolved },
    { label: 'Critical', value: w.critical, alert: w.critical > 0 },
    { label: 'Overdue', value: w.overdue, alert: w.overdue > 0 },
    { label: 'Avg age', value: `${w.avgAge}d` },
  ]

  return (
    <DetailOverlay backLabel={backLabel} onBack={onBack} refText={department}>
      <HodHero department={department} hodName={hodName} hodPhone={hodPhone} hodEmail={hodEmail} />

      <div className="mb-[14px]"><DetailStatStrip stats={stats} /></div>

      <div className="mb-5 flex items-start gap-[11px] rounded-[13px] border border-gis-line bg-white px-[14px] py-3">
        <Layers className="mt-px h-[18px] w-[18px] flex-none stroke-2 text-gis-soft" />
        <p className="text-[11.5px] leading-[1.5] text-gis-ink-2">
          <b className="font-bold text-gis-ink">{w.unresolved} unresolved issue{w.unresolved === 1 ? '' : 's'}</b> across {w.siteCount} site{w.siteCount === 1 ? '' : 's'}. {w.critical} critical, {w.overdue} overdue. Oldest is <b className="font-bold text-gis-ink">{w.oldest} days</b>.
        </p>
      </div>

      <div className="mb-3 flex items-center justify-between">
        <div className="text-[10.5px] font-bold tracking-[.08em] text-gis-heading uppercase">Issues · {w.unresolved}</div>
        <button type="button" onClick={() => setSort(NEXT[sort])} className="flex items-center gap-[7px] rounded-[9px] border border-gis-line bg-white px-[11px] py-[7px] text-[11.5px] font-semibold text-gis-ink-2 transition hover:border-[#d6dbe6] hover:bg-[#f7f9fc] focus-visible:ring-2 focus-visible:ring-blue-500/60">
          <ArrowUpDown className="h-3.5 w-3.5 stroke-2 text-gis-muted" /> Sort · <b className="font-bold text-gis-ink">{SORT_LABEL[sort]}</b>
        </button>
      </div>

      {isPending ? (
        <div className="text-muted-text py-6 text-center text-xs">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-muted-text py-6 text-center text-[12px]">No open issues for this department.</div>
      ) : (
        <div ref={rootRef} className="space-y-1">
          {rows.map((r) => (
            <Fragment key={r.id}>
              <HodRow r={r} isExpanded={expandedId === r.id} onToggle={() => toggle(r.id)} />
              {expandedId === r.id && (
                <IssueInlineDetail
                  issueId={r.id}
                  sites={sites}
                  scope={scope}
                  backLabel="Back to department"
                  onBack={collapse}
                  // This department's workload is already on screen — don't stack a duplicate HOD frame.
                  onOpenHod={(h) => {
                    if (h.department !== department) {
                      onOpenHod(h)
                    }
                  }}
                  onSelectOfficer={onSelectOfficer}
                  onSelectMandal={onSelectMandal}
                />
              )}
            </Fragment>
          ))}
        </div>
      )}
    </DetailOverlay>
  )
}

function HodRow({ r, isExpanded, onToggle }: { r: WorkloadRow; isExpanded: boolean; onToggle: () => void }) {
  const barW = Math.max(6, Math.min(100, Math.round((r.age / 30) * 100)))
  const barColor = r.flag === 'overdue' ? '#dc2626' : r.flag === 'due-soon' ? '#f59e0b' : '#94a3b8'
  const pending = r.status === 'pending'
  return (
    <div data-issue-row={r.id}>
      <button type="button" aria-expanded={isExpanded} onClick={onToggle} className="flex w-full items-center gap-[13px] rounded-[12px] px-3 py-[13px] text-left transition hover:bg-[#f7f9fc] focus-visible:ring-2 focus-visible:ring-blue-500/60">
      <div className={`grid h-[26px] w-[26px] flex-none place-items-center rounded-[8px] text-[11.5px] font-bold text-white ${SEV_TOKEN[r.severity]}`}>{r.severity.charAt(0).toUpperCase()}</div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-[12.5px] font-semibold text-gis-ink">{r.title}</div>
        <div className="mt-[3px] truncate text-[11px] font-medium text-gis-soft">{r.siteName}{r.mandal ? ` · ${prettyName(r.mandal)}` : ''}</div>
        <div className="mt-[9px] h-[5px] overflow-hidden rounded-full bg-[#eef1f6]"><i className="block h-full rounded-full" style={{ width: `${barW}%`, background: barColor }} /></div>
      </div>
      <div className="flex flex-none flex-col items-end gap-1">
        <DetailChip size="sm" className={`tracking-[.03em] uppercase ${pending ? 'border-[#fed7aa] bg-gis-amber-tint text-[#b45309]' : 'border-[#e2e8f0] bg-[#f1f5f9] text-[#475569]'}`}>{pending ? 'Pending' : 'Open'}</DetailChip>
        <div className="flex items-center gap-1 text-[12px] font-bold text-gis-ink-2 tabular-nums">
          {r.age}d
          {r.flag !== 'on-track' && <AlertTriangle className={`h-[11px] w-[11px] stroke-[2.4] ${r.flag === 'overdue' ? 'text-gis-red' : 'text-[#b45309]'}`} />}
        </div>
      </div>
      </button>
    </div>
  )
}
